"use client";

import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { Suspense, useEffect, useRef } from "react";
import Navbar from "@/elements/Navbar";
import WhatsAppButton from "@/elements/WhatsAppButton";
import FloatingSocialLinks from "@/components/FloatingSocialLinks";
import ShopFloatingSocialLinks from "@/components/shop/ShopFloatingSocialLinks";
import { useAppDispatch } from "@/store/hooks";
import { fetchGeneralSettings } from "@/store/slices/landingSlice";
import { fetchAllShopSettings } from "@/store/slices/shopSettingsSlice";

// Dynamically import heavy footer components
const Footer = dynamic(() => import("@/elements/Footer"), {
    ssr: false,
    loading: () => <div className="h-40 bg-gray-900/5 dark:bg-black/5 animate-pulse" />
});
const ShopFooter = dynamic(() => import("@/elements/ShopFooter"), {
    ssr: false,
    loading: () => <div className="h-40 bg-gray-900/5 dark:bg-black/5 animate-pulse" />
});

export default function MainLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const pathname = usePathname();
    const dispatch = useAppDispatch();
    const isShopPage = pathname?.startsWith("/shop");
    const isCheckoutPage = pathname?.startsWith("/checkout");
    const isCartPage = pathname?.startsWith("/cart");
    const isWishlistPage = pathname?.startsWith("/wishlist");
    const isOrdersPage = pathname?.startsWith("/orders");
    const isProfilePage = pathname?.startsWith("/profile");
    const isSettingsPage = pathname?.startsWith("/settings");
    const isLiveChatPage = pathname?.startsWith("/live-chat");
    const isHelpPage = pathname?.startsWith("/help");
    const isTrackOrderPage = pathname?.startsWith("/track-order");
    
    const isShopFooterNeeded = isShopPage || isCheckoutPage || isCartPage || isWishlistPage || isOrdersPage || isProfilePage || isSettingsPage || isLiveChatPage || isHelpPage || isTrackOrderPage;

    // Fetch each settings payload at most once per session — without these
    // guards, navigating back and forth between shop and non-shop pages
    // re-dispatched both fetches on every crossing, since this layout
    // persists across (main) route navigations and isShopFooterNeeded flips.
    const hasFetchedGeneralSettings = useRef(false);
    const hasFetchedShopSettings = useRef(false);

    useEffect(() => {
        if (!hasFetchedGeneralSettings.current) {
            dispatch(fetchGeneralSettings());
            hasFetchedGeneralSettings.current = true;
        }
        if (isShopFooterNeeded && !hasFetchedShopSettings.current) {
            dispatch(fetchAllShopSettings());
            hasFetchedShopSettings.current = true;
        }
    }, [dispatch, isShopFooterNeeded]);

    return (
        <>
            {!isShopFooterNeeded && <Navbar />}
            <main className="overflow-x-hidden w-full max-w-full">{children}</main>
            <Suspense fallback={<div className="h-20" />}>
                {isShopFooterNeeded ? <ShopFooter /> : <Footer />}
            </Suspense>
            {isShopFooterNeeded ? <ShopFloatingSocialLinks /> : <FloatingSocialLinks />}
            <WhatsAppButton />
        </>
    );
}
